// The Layout Canvas's keyboard and mouse shortcuts, as the one list the "?" guide shows.
//
// Documentation only — the real bindings live in TakeoffCanvas.jsx's key handler. So the guide cannot drift from
// them, an entry that is a KEY binding carries `pin`: source snippets that must appear in that handler, and
// src/test/canvasShortcuts.test.js fails when a snippet is missing (a binding changed or removed without the guide).
// Entries for the mouse and for held modifiers have no pin (there is no single place to point at).
//
// Each entry: { keys: ['Ctrl', 'Z'], what: 'Undo', pin?: ['...'] } — keys render as separate caps; "+" is never a key.

export const CANVAS_SHORTCUT_SECTIONS = Object.freeze([
  {
    title: "Tracing and drawing",
    items: [
      { keys: ["Enter"], what: "Finish the shape", pin: [`e.key === "Enter"`, "finishConditionRun()"] },
      { keys: ["Backspace"], what: "Remove the last point", pin: [`e.key === "Backspace"`] },
      { keys: ["Esc"], what: "Back out one level: the last point first, then the tool, then the Room Details panel", pin: [`e.key === "Escape"`] },
      { keys: ["Shift"], what: "Hold while clicking: free angle (no 22.5° lock)" },
      { keys: ["Alt"], what: "Hold while clicking: no snapping" },
      { keys: ["Double-click"], what: "Finish the shape" },
      { keys: ["D"], what: "Draw a room", pin: [`e.key === "d" || e.key === "D"`] },
      { keys: ["1–9"], what: "Arm the pinned condition with that number (the strip under the toolbar)", pin: ["/^[1-9]$/.test(e.key)"] },
    ],
  },
  {
    title: "Selection",
    items: [
      { keys: ["Delete"], what: "Remove the selected condition or symbol (several at once = one undo). Rooms are deleted from the right-click menu. Backspace does the same when no shape is being traced", pin: [`e.key === "Delete"`] },
      { keys: ["S"], what: "Flip the selected door or window symbol", pin: [`e.key === "s" || e.key === "S"`] },
      { keys: ["R"], what: "Rotate the selected room", pin: [`e.key === "r" || e.key === "R"`] },
      { keys: ["Shift", "Click"], what: "Add or remove a room — or a drawn condition — from the selection" },
      { keys: ["Ctrl", "Click"], what: "Add or remove a drawn condition from the selection (same as Shift)" },
      { keys: ["Ctrl", "C"], what: "Copy the selected conditions", pin: [`(e.key === "c" || e.key === "C") && selectedCondIds.size > 0`] },
      { keys: ["Ctrl", "V"], what: "Paste them at the cursor — on this drawing or another one", pin: [`(e.key === "v" || e.key === "V") && clipboardRef.current?.length`] },
      { keys: ["Ctrl", "D"], what: "Duplicate the selected conditions beside the originals", pin: [`(e.key === "d" || e.key === "D") && selectedCondIds.size > 0`] },
      { keys: ["Ctrl", "R"], what: "Rotate the selected conditions 90° clockwise about their centre (Shift = counter-clockwise)", pin: [`transformSelectedConditions(rows, { type: "rotate"`] },
      { keys: ["Ctrl", "S"], what: "Spin the selected conditions in place by an angle you enter", pin: [`spinPrompt()`] },
      { keys: ["Ctrl", "Shift", "H"], what: "Flip the selected conditions horizontally", pin: [`k === "h" ? "flipH" : "flipV"`] },
      { keys: ["Ctrl", "Shift", "V"], what: "Flip the selected conditions vertically", pin: [`e.key === "v" || e.key === "V"`] },
      { keys: ["Right-click"], what: "Menu for the room or condition under the cursor — room: details, lock position, delete; condition: details, duplicate, copy, rotate / flip, delete (also on the Rooms and Conditions docks)" },
      { keys: ["Drag", "a room"], what: "Move it — only after a short drag, and not at all while its position is locked (right-click ▸ Unlock position). Rooms you draw start locked" },
      { keys: ["Drag", "a door / window"], what: "Slide it along the wall line it sits on, to line it up with the drawing; hold Alt to pull it onto a different wall instead" },
      { keys: ["Double-click", "room in dock"], what: "Open its Room Details (Rooms dock)" },
      { keys: ["T"], what: "Trace another one like the selected condition", pin: [`(e.key === "t" || e.key === "T") && selectedCondId`] },
      { keys: ["Double-click", "corner"], what: "Remove that corner of an outline" },
    ],
  },
  {
    title: "View",
    items: [
      { keys: ["+"], what: "Zoom in (the = key too)", pin: [`e.key === "=" || e.key === "+"`] },
      { keys: ["-"], what: "Zoom out", pin: [`e.key === "-" || e.key === "_"`] },
      { keys: ["F"], what: "Fit the drawing to the window", pin: [`e.key === "f" || e.key === "F"`] },
      { keys: ["M"], what: "Focus mode — hide the ribbon and panels so only the drawing shows (M or Esc returns)", pin: [`e.key === "m" || e.key === "M"`] },
      { keys: ["Z"], what: "Zoom to the selected rooms", pin: [`e.key === "z" || e.key === "Z") && selectedIds.size > 0`] },
      { keys: ["Wheel"], what: "Pan the drawing up and down" },
      { keys: ["Shift", "Wheel"], what: "Pan the drawing left and right" },
      { keys: ["Ctrl", "Wheel"], what: "Zoom toward the cursor (a trackpad pinch does the same)" },
      { keys: ["Drag"], what: "Pan the drawing" },
    ],
  },
  {
    title: "Drawings",
    items: [
      { keys: ["["], what: "Previous drawing", pin: [`e.key === "[" || e.key === "]"`] },
      { keys: ["]"], what: "Next drawing", pin: [`e.key === "[" || e.key === "]"`] },
    ],
  },
  {
    title: "Edit and find",
    items: [
      { keys: ["Ctrl", "Z"], what: "Undo", pin: [`e.key === "z" || e.key === "Z")) { e.preventDefault(); if (isLocked) return;`] },
      { keys: ["Ctrl", "Shift", "Z"], what: "Redo", pin: [`if (e.shiftKey) { if (undoInfo?.canRedo) onRedo?.(); }`] },
      { keys: ["Ctrl", "Y"], what: "Redo", pin: [`e.key === "y" || e.key === "Y"`] },
      { keys: ["Ctrl", "K"], what: "Find a tool, condition, drawing, scale or action", pin: [`e.key === "k" || e.key === "K"`] },
      { keys: ["?"], what: "Show this guide", pin: [`e.key === "?"`] },
    ],
  },
]);

/** Every pinned snippet, flat — what the drift test looks for in the canvas source. */
export const shortcutPins = () => CANVAS_SHORTCUT_SECTIONS.flatMap(s => s.items.flatMap(i => i.pin || []));
